// Libraries
import React from 'react';

// Material UI Components
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

// Local Files
import TypingAnimation from './typingAnimation.js';

// Material UI Icons
import AndroidIcon from '@mui/icons-material/Android';
import PersonIcon from '@mui/icons-material/Person';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import InfoIcon from '@mui/icons-material/Info';
import { MemoizedReactMarkdown } from './markdown/memoizedReactMarkdown.js';
import CodeBlock from './markdown/codeBlock.js';
import ImageBlock from './markdown/imageBlock.js';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeMathjax from 'rehype-mathjax';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught in ErrorBoundary: ", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h2>Something went wrong with the markdown rendering.</h2>;
    }
    return this.props.children;
  }
}

// Styles constants
const styles = {
  userBgColor: '#343542',
  assistantBgColor: 'rgb(82 83 96)',
  systemBgColor: 'rgb(60, 60, 60)',
  textColor: '#fff',
  linkColor: '#24ADE4',
  borderColor: '#e3e3e3',
  headerBgColor: '#e3e3e3',
  headerColor: 'rgb(82 83 96)',
  actionColor: '#24ADE4',
};

// Push to CMS unsupported service types
const PUSH_TO_CUE_BLOCKLIST = [
  'User Needs Categoriser (News Model)',
  'User Needs Categoriser (Standard Model)',
  'Headline Topic and Theme Analyser',
  'Headline Analysis and Optimisation',
  'Content and Headline Analysis',
  'Summarise This TL;DR',
  'Story Ideas',
  'Title Suggestions',
  'Writers Assistant',
];
const isPushToCueBlocked = (serviceType) =>
  !!serviceType && PUSH_TO_CUE_BLOCKLIST.some(
    s => s.toLowerCase() === String(serviceType).toLowerCase()
  );

// Utility function to get icon based on role
const getIcon = (role) => {
  switch (role) {
    case 'user':
      return <PersonIcon sx={{ color: styles.textColor, mt: 2 }} />;
    case 'assistant':
      return <AndroidIcon sx={{ color: styles.textColor, mt: 2 }} />;
    case 'system':
      return <InfoIcon sx={{ color: styles.textColor, mt: 2 }} />;
    default:
      return null;
  }
};

/** Push-to-CMS button with tooltip, spinner, and direct handleAction call */
const SendToCMSButton = ({ chat, disabled, handleAction }) => {
  const [sending, setSending] = React.useState(false);

  const handleClick = async (e) => {
    if (disabled || sending) return;

    if (typeof handleAction !== 'function') {
      alert('Action handler is not configured.');
      return;
    }

    const text = (chat?.content || '').trim();
    if (!text || text === 'Mantis Scribe is Typing...') {
      const proceed = window.confirm('This message looks empty. Push to CMS anyway?');
      if (!proceed) return;
    }

    try {
      setSending(true);
      const maybePromise = handleAction('sendToCue', chat);
      if (maybePromise && typeof maybePromise.then === 'function') {
        await maybePromise;
      }
    } catch (err) {
      alert('Failed to push to CMS. Check console for details.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Tooltip
      title={disabled ? "Push to CMS not supported for this service type" : "Push to CMS (⌘⏎ / Ctrl⏎)"}
      arrow
      placement="bottom-end"
      enterDelay={250}
      PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, 6] } }] }}
    >
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          aria-label="Push to CMS"
          onClick={handleClick}
          disabled={disabled || sending}
          size="small"
          sx={{
            color: (disabled || sending) ? 'action.disabled' : styles.actionColor,
          }}
        >
          {sending ? <CircularProgress size={18} /> : <RocketLaunchOutlinedIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ChatMessage Component
const ChatMessage = ({ chat, chatConfig, index, handleAction }) => {
  const { role, content } = chat;
  const isUser = role === 'user';
  const isAssistant = role === 'assistant';
  const isSystem = role === 'system';
  const isTyping = chat?.isGenerating || chat?.streaming || content === 'Mantis Scribe is Typing...';
  const activeModel = chatConfig?.options?.model || 'latest';

  const allowedTeam =
    chatConfig?.settings?.team === 'Editorial' ||
    chatConfig?.settings?.team === 'Commercial Editorial';

  const serviceType = chatConfig?.settings?.serviceType;
  const pushBlocked = isPushToCueBlocked(serviceType);

  // Keyboard shortcut
  React.useEffect(() => {
    const onKeyDown = (e) => {
      const metaEnter = (e.metaKey || e.ctrlKey) && e.key === 'Enter';
      if (metaEnter && allowedTeam && !pushBlocked && isAssistant && !isTyping) {
        e.preventDefault();
        if (typeof handleAction === 'function') {
          handleAction('sendToCue', chat);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chat, allowedTeam, pushBlocked, isAssistant, isTyping, handleAction]);

  return (
    <Box
      key={index}
      display="flex"
      alignItems="flex-start"
      mb={2}
      className={`message ${isUser ? 'user-message' : isAssistant ? 'assistant-message' : 'system-message'}`}
      sx={{
        backgroundColor: isUser ? styles.userBgColor : isAssistant ? styles.assistantBgColor : styles.systemBgColor,
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1,
        maxWidth: '100%',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
      }}
    >
      {getIcon(role)}
      <Box sx={{ ml: 1, color: styles.textColor, maxWidth: 'calc(100% - 50px)', overflowWrap: 'break-word', wordWrap: 'break-word', wordBreak: 'break-word' }}>
        {isTyping ? (
          <TypingAnimation model={activeModel} isGenerating={true} />
        ) : (
          <ErrorBoundary>
            <MemoizedReactMarkdown
              className="prose dark:prose-invert"
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeMathjax]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline && match ? (
                    <CodeBlock
                      language={match[1]}
                      value={String(children).replace(/\n$/, '')}
                      {...props}
                    />
                  ) : (
                    <code
                      className={className}
                      style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        color: styles.textColor,
                      }}
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                a({ href, children, ...props }) {
                  return (
                    <a
                      href={href}
                      style={{
                        color: styles.linkColor,
                        textDecoration: 'underline',
                      }}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                },
                table({ children }) {
                  return (
                    <Box
                      component="div"
                      sx={{
                        overflowX: 'auto',
                        display: 'block',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        borderCollapse: 'collapse',
                        border: `1px solid ${styles.borderColor}`,
                      }}
                    >
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                        }}
                        className="table-auto"
                      >
                        {children}
                      </table>
                    </Box>
                  );
                },
                thead({ children }) {
                  return (
                    <thead
                      style={{
                        backgroundColor: styles.headerBgColor,
                      }}
                    >
                      {children}
                    </thead>
                  );
                },
                tbody({ children }) {
                  return (
                    <tbody
                      style={{
                        backgroundColor: 'inherit',
                      }}
                    >
                      {children}
                    </tbody>
                  );
                },
                tr({ children, ...props }) {
                  return (
                    <tr
                      style={{
                        borderBottom: `1px solid ${styles.borderColor}`,
                      }}
                      {...props}
                    >
                      {children}
                    </tr>
                  );
                },
                th({ children }) {
                  return (
                    <th
                      style={{
                        padding: '8px 12px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: styles.headerColor,
                        borderBottom: `1px solid ${styles.borderColor}`,
                      }}
                    >
                      {children}
                    </th>
                  );
                },
                td({ children }) {
                  return (
                    <td
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        color: styles.textColor,
                        borderBottom: `1px solid ${styles.borderColor}`,
                      }}
                    >
                      {children}
                    </td>
                  );
                },
                img({ src, alt }) {
                  return <ImageBlock src={src} alt={alt} />;
                },
              }}
            >
              {content}
            </MemoizedReactMarkdown>
          </ErrorBoundary>
        )}

        {isAssistant && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              zIndex: (theme) => (theme?.zIndex?.modal ?? 1300) + 2,
            }}
          >
            <Tooltip title="Copy to clipboard" arrow placement="bottom-end" enterDelay={250}>
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  aria-label="Copy"
                  onClick={(e) => { e.stopPropagation(); handleAction && handleAction('copy', chat); }}
                  size="small"
                  sx={{ color: styles.textColor }}
                >
                  <FileCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>

            {allowedTeam && (
              <SendToCMSButton
                chat={chat}
                disabled={isTyping || pushBlocked}
                handleAction={handleAction}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
